import { formatDistanceToNow } from 'date-fns';
import { sv } from 'date-fns/locale';
import type { HandOffConversation, HandoffStatus } from '../../types/handoff';

type Props = {
  conversation: HandOffConversation;
  isSelected: boolean;
  onSelect: () => void;
};

function getChannelLabel(channel: string | null) {
  switch (channel) {
    case 'webchat':
    case 'website':
      return 'WEB';
    case 'email':
      return 'EM';
    default:
      return '—';
  }
}

function getStatusLabel(status: HandoffStatus | null) {
  switch (status) {
    case 'requested':
      return 'Ny';
    case 'assigned':
      return 'Tilldelad';
    case 'active':
      return 'Aktiv';
    case 'resolved':
      return 'Löst';
    case 'mailed':
      return 'Mejl misslyckades';
    default:
      return status ?? '—';
  }
}

export function HandoffQueueItem({ conversation, isSelected, onSelect }: Props) {
  const activityAt = conversation.updatedAt ?? conversation.requestedAt;
  const timeAgo = activityAt
    ? formatDistanceToNow(new Date(activityAt), { addSuffix: true, locale: sv })
    : '';

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={[
          'w-full px-4 py-3 text-left transition',
          isSelected ? 'bg-blue-50' : 'bg-white hover:bg-gray-50',
        ].join(' ')}
      >
        <div className="flex items-start gap-3">
          <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-amber-500" />

          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-3">
              <div className="truncate text-sm font-semibold text-gray-950">
                {conversation.customerName || 'Okänd kund'}
              </div>

              <div className="shrink-0 text-xs text-gray-400">{timeAgo}</div>
            </div>

            <div className="mt-1 flex items-center gap-2">
              <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-gray-500">
                {getChannelLabel(conversation.channel)}
              </span>
              <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-700">
                {getStatusLabel(conversation.handoffStatus)}
              </span>
              {conversation.caseType === 'email_draft' ? (
                <span
                  className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-blue-700"
                  title="AI-utkastat kundmejl – kunden har godkänt och skickat via chatten, svara via mail"
                >
                  KUNDMEJL
                </span>
              ) : (
                conversation.isMailHandoff && (
                  <span
                    className="rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-orange-700"
                    title="Inkom utanför öppettid – hanteras via mail, svara inte i chatten"
                  >
                    MAIL
                  </span>
                )
              )}
              {(conversation.escalationEmailError ||
                conversation.emailDraftStatus === 'failed') && (
                <span
                  className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-red-700"
                  title={`Mejlet till kundtjänst gick inte ut – hantera manuellt${
                    conversation.escalationEmailError || conversation.emailDraftLastError
                      ? ` (${conversation.escalationEmailError || conversation.emailDraftLastError})`
                      : ''
                  }`}
                >
                  EJ SKICKAT
                </span>
              )}
            </div>

            <p className="mt-2 line-clamp-2 text-xs leading-5 text-gray-500">
              {conversation.lastMessage || 'Ingen sammanfattning ännu.'}
            </p>
          </div>
        </div>
      </button>
    </li>
  );
}
